import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  CogIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  DocumentArrowUpIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PremiumUpgradeSuccessModal from '../../components/ui/PremiumUpgradeSuccessModal';
import PremiumUpgradeModal from '../../components/ui/PremiumUpgradeModal';
import CaseUpdateProgressBar, { CaseUpdateStep } from '../../components/ui/CaseUpdateProgressBar';
import { displaySafeFilename } from '../../utils/fileEncoding';
import { appendFileToFormData } from '../../utils/fileEncoding';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Lawsuit as SharedLawsuit, AIQuestion } from '@tvia-ai/shared';

const StrengthenCasePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Updated state to work with embedded answers in questions
  const [questionsWithUpdates, setQuestionsWithUpdates] = useState<Record<string, AIQuestion>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File[]>>({});
  const [submittedQuestions, setSubmittedQuestions] = useState<Set<string>>(new Set()); // Track actually submitted questions
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [hasGeneratedQuestions, setHasGeneratedQuestions] = useState(false);

  const [answeredQuestionsCollapsed, setAnsweredQuestionsCollapsed] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPremiumSuccessModal, setShowPremiumSuccessModal] = useState(false);
  const [premiumUpgradeData, setPremiumUpgradeData] = useState<any>(null);

  // Track which question is being edited
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  // Case update state
  const [showUpdateCaseModal, setShowUpdateCaseModal] = useState(false);
  const [updateCaseData, setUpdateCaseData] = useState({
    updateType: 'evidence',
    description: '',
    accusedPartyResponse: '',
    files: [] as File[]
  });

  // Multi-step process state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSteps, setProgressSteps] = useState<CaseUpdateStep[]>([]);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);


  // Fetch lawsuit details
  const { data: lawsuit, isLoading, error } = useQuery<SharedLawsuit | undefined>({
    queryKey: ['lawsuit', id],
    queryFn: async () => {
      if (!id) return undefined;
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data.data.lawsuit as SharedLawsuit;
    },
    enabled: !!id,
  });

  // Generate additional questions mutation
  const generateQuestionsMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/lawsuits/${id}/generate-questions`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the lawsuit data to get new questions
      queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
      setHasGeneratedQuestions(true);
    },
  });

  // Auto-refresh when questions are generated to show them immediately
  useEffect(() => {
    if (hasGeneratedQuestions) {
      // Small delay to ensure the backend has processed the new questions
      const timer = setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['lawsuit', id] });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [hasGeneratedQuestions, queryClient, id]);

  // Initialize questionsWithUpdates with existing answers for editing
  useEffect(() => {
    if (lawsuit?.followUpQuestions) {
      const initialQuestions: Record<string, AIQuestion> = {};
      const alreadySubmitted = new Set<string>();
      
      lawsuit.followUpQuestions.forEach(question => {
        initialQuestions[question.id] = question;
        // If question has answeredAt from server, it's already submitted
        if (question.answeredAt) {
          alreadySubmitted.add(question.id);
        }
      });
      
      setQuestionsWithUpdates(prev => ({
        ...initialQuestions,
        ...prev // Keep any unsaved changes
      }));
      
      setSubmittedQuestions(alreadySubmitted);
    }
  }, [lawsuit?.followUpQuestions]);

  // Submit responses mutation
  const submitResponsesMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await axios.post(`/api/lawsuits/${id}/responses`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
      
      // Show success message with strength update
      if (data.strengthUpdate) {
        alert(t('lawsuit.strengthUpdated', 'Your case strength has been updated! New documents have been generated based on your responses.'));
      }
      
      navigate(`/lawsuit/${id}`);
    },
  });

  // Premium analysis upgrade mutation
  const premiumAnalysisMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/lawsuits/${id}/premium-analysis`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
      setPremiumUpgradeData(data.data);
      setShowPremiumSuccessModal(true);
    },
  });

  // Step 1: Update case mutation
  const updateCaseMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await axios.post(`/api/lawsuits/${id}/update-case`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
  });

  // Step 2: Analyze case mutation
  const analyzeCaseMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/lawsuits/${id}/analyze-case`);
      return response.data;
    },
  });

  // Step 3: Generate documents mutation
  const generateDocumentsMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/lawsuits/${id}/generate-documents`);
      return response.data;
    },
  });

  const handleUpgradeToPremium = async () => {
    await premiumAnalysisMutation.mutateAsync();
    setShowPremiumModal(false);
  };

  const handleOpenPremiumModal = () => {
    setShowPremiumModal(true);
  };

  const handleUpdateCaseSubmit = async () => {
    // Initialize progress steps
    const steps: CaseUpdateStep[] = [
      {
        id: 'update-case',
        name: t('lawsuit.updatingCase', 'Updating Case'),
        status: 'pending',
        description: t('lawsuit.updatingCaseDescription', 'Storing your case update information...')
      },
      {
        id: 'analyze-case',
        name: t('lawsuit.analyzingCase', 'Analyzing Case'),
        status: 'pending',
        description: t('lawsuit.analyzingCaseDescription', 'Re-analyzing your case with new information...')
      },
      {
        id: 'generate-documents',
        name: t('lawsuit.generatingDocuments', 'Generating Documents'),
        status: 'pending',
        description: t('lawsuit.generatingDocumentsDescription', 'Creating updated legal documents...')
      }
    ];

    setProgressSteps(steps);
    setShowProgressModal(true);
    setShowUpdateCaseModal(false);
    setProcessError(null);

    try {
      // Step 1: Update case
      setCurrentStepId('update-case');
      setProgressSteps(prev => prev.map(step => 
        step.id === 'update-case' ? { ...step, status: 'in_progress' } : step
      ));

      const formData = new FormData();
      formData.append('updateType', updateCaseData.updateType);
      formData.append('description', updateCaseData.description);
      if (updateCaseData.accusedPartyResponse) {
        formData.append('accusedPartyResponse', updateCaseData.accusedPartyResponse);
      }
      
      // Add uploaded files
      updateCaseData.files.forEach((file, index) => {
        appendFileToFormData(formData, 'files', file);
      });

      await updateCaseMutation.mutateAsync(formData);

      // Mark step 1 as completed
      setProgressSteps(prev => prev.map(step => 
        step.id === 'update-case' ? { ...step, status: 'completed' } : step
      ));

      // Step 2: Analyze case
      setCurrentStepId('analyze-case');
      setProgressSteps(prev => prev.map(step => 
        step.id === 'analyze-case' ? { ...step, status: 'in_progress' } : step
      ));

      const analysisResult = await analyzeCaseMutation.mutateAsync();

      // Mark step 2 as completed
      setProgressSteps(prev => prev.map(step => 
        step.id === 'analyze-case' ? { ...step, status: 'completed' } : step
      ));

      // Step 3: Generate documents
      setCurrentStepId('generate-documents');
      setProgressSteps(prev => prev.map(step => 
        step.id === 'generate-documents' ? { ...step, status: 'in_progress' } : step
      ));

      const documentsResult = await generateDocumentsMutation.mutateAsync();

      // Mark step 3 as completed
      setProgressSteps(prev => prev.map(step => 
        step.id === 'generate-documents' ? { ...step, status: 'completed' } : step
      ));

      // Refresh lawsuit data
      queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });

      // Reset form
      setUpdateCaseData({
        updateType: 'evidence',
        description: '',
        accusedPartyResponse: '',
        files: []
      });

      // Show success message
      setTimeout(() => {
        setShowProgressModal(false);
        alert(t('lawsuit.caseUpdateComplete', 'Your case has been successfully updated! New documents have been generated based on the additional information.'));
      }, 2000);

    } catch (error: any) {
      console.error('Error in case update process:', error);
      setProcessError(error.message || t('lawsuit.caseUpdateError', 'An error occurred while updating your case. Please try again.'));
      
      // Mark current step as error
      setProgressSteps(prev => prev.map(step => 
        step.id === currentStepId ? { ...step, status: 'error' } : step
      ));
    }
  };

  const handleUpdateCaseFileUpload = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setUpdateCaseData(prev => ({
        ...prev,
        files: [...prev.files, ...fileArray]
      }));
    }
  };

  const removeUpdateCaseFile = (index: number) => {
    setUpdateCaseData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleResponseChange = (questionId: string, value: any) => {
    setQuestionsWithUpdates(prev => {
      const existingQuestion = prev[questionId] || lawsuit?.followUpQuestions?.find(q => q.id === questionId);
      if (!existingQuestion) return prev;
      
      return {
        ...prev,
        [questionId]: { 
          ...existingQuestion, 
          answer: value
          // Removed answeredAt - only set on actual submission
        }
      };
    });
  };

  const handleFileUpload = (questionId: string, files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setUploadedFiles(prev => ({
        ...prev,
        [questionId]: fileArray
      }));
    }
  };

  const handleGenerateQuestions = async () => {
    setIsGeneratingQuestions(true);
    try {
      await generateQuestionsMutation.mutateAsync();
      // Reset the hasGeneratedQuestions flag after a short delay to allow regeneration
      setTimeout(() => {
        setHasGeneratedQuestions(false);
      }, 5000); // Hide success message after 5 seconds
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleSubmitResponses = async () => {
    const formData = new FormData();
    
    // Build answers with file name mapping (matching AIAnalysisStep pattern)
    const answersWithFiles: Record<string, any> = {};
    const submissionTime = new Date(); // Single timestamp for all submitted answers
    const questionsBeingSubmitted = new Set<string>(); // Track which questions are being submitted
    
    // Process ALL questions that have ANY content (answers or files)
    Object.entries(questionsWithUpdates)
      .filter(([questionId, question]) => {
        const hasTextAnswer = question.answer !== undefined && question.answer !== null && question.answer !== '';
        const hasNewFiles = uploadedFiles[questionId]?.length > 0;
        const hasExistingFiles = getQuestionFiles(question).length > 0;
        return hasTextAnswer || hasNewFiles || hasExistingFiles;
      })
      .forEach(([questionId, question]) => {
        // Combine new files and existing files
        const newFiles = uploadedFiles[questionId] || [];
        const existingFiles = getQuestionFiles(question);
        
        // Get all file names (new files + existing files)
        const newFileNames = newFiles.map(file => file.name);
        const existingFileNames = existingFiles.map(file => 
          file.filename || file.originalName || (file as any).name || 'unknown'
        );
        const allFileNames = [...newFileNames, ...existingFileNames];
        
        answersWithFiles[questionId] = {
          answer: question.answer || '',
          files: allFileNames
        };
        
        questionsBeingSubmitted.add(questionId);
        
        // Update local state with submission timestamp
        setQuestionsWithUpdates(prev => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            answeredAt: submissionTime
          }
        }));
      });
    
    // Also include questions that only have new file uploads (no existing question in questionsWithUpdates)
    Object.entries(uploadedFiles).forEach(([questionId, files]) => {
      if (files.length > 0 && !answersWithFiles[questionId]) {
        const fileNames: string[] = files.map(file => file.name);
        answersWithFiles[questionId] = {
          answer: '', // No text answer, just files
          files: fileNames
        };
        
        questionsBeingSubmitted.add(questionId);
        
        // Update local state with submission timestamp
        setQuestionsWithUpdates(prev => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            answeredAt: submissionTime
          }
        }));
      }
    });
    
    // Use 'responses' key to match backend expectation
    formData.append('responses', JSON.stringify(answersWithFiles));
    
    // Add uploaded files using the same method as AIAnalysisStep
    Object.entries(uploadedFiles).forEach(([, files]) => {
      files.forEach((file) => {
        appendFileToFormData(formData, 'files', file);
      });
    });

    try {
      await submitResponsesMutation.mutateAsync(formData);
      
      // Only mark questions as submitted after successful API call
      setSubmittedQuestions(prev => {
        const newSubmitted = new Set(prev);
        questionsBeingSubmitted.forEach(questionId => newSubmitted.add(questionId));
        return newSubmitted;
      });
      
      // Clear uploaded files for submitted questions
      setUploadedFiles(prev => {
        const updated = { ...prev };
        questionsBeingSubmitted.forEach(questionId => {
          delete updated[questionId];
        });
        return updated;
      });
      
    } catch (error) {
      // If submission fails, revert the answeredAt timestamps
      setQuestionsWithUpdates(prev => {
        const updated = { ...prev };
        questionsBeingSubmitted.forEach(questionId => {
          if (updated[questionId]) {
            delete updated[questionId].answeredAt;
          }
        });
        return updated;
      });
      throw error; // Re-throw so the mutation error handling works
    }
  };

  const renderQuestionInput = (question: AIQuestion) => {
    const updatedQuestion = getQuestionWithUpdates(question);
    const value = updatedQuestion.answer || '';
    // Get actual file objects by matching string array with lawsuit files
    const existingFiles = getQuestionFiles(updatedQuestion);
    const newFiles = uploadedFiles[question.id] || [];

    const renderFileUploadSection = () => (
      <div className="mt-4 border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          {t('lawsuit.evidence', 'Supporting Evidence')}
        </h4>
        <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor={`file-upload-${question.id}`}
                className="relative cursor-pointer bg-white rounded-md font-medium text-cyan-600 hover:text-cyan-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-cyan-500"
              >
                <span>{t('lawsuit.uploadFiles', 'Upload files')}</span>
                <input
                  id={`file-upload-${question.id}`}
                  name={`file-upload-${question.id}`}
                  type="file"
                  className="sr-only"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => handleFileUpload(question.id, e.target.files)}
                />
              </label>
              <p className="pl-1">{t('lawsuit.orDragDrop', 'or drag and drop')}</p>
            </div>
            <p className="text-xs text-gray-500">
              {t('lawsuit.fileTypes', 'PDF, DOC, DOCX, TXT, JPG, PNG, GIF up to 10MB each')}
            </p>
          </div>
        </div>
        {newFiles.length > 0 && (
          <div className="mt-2">
            <h4 className="text-sm font-medium text-gray-900">{t('lawsuit.uploadedFiles', 'Uploaded Files')}:</h4>
            <ul className="mt-1 space-y-1">
              {newFiles.map((file: File, index: number) => (
                <li key={index} className="text-sm text-gray-600 flex items-center justify-between">
                                        <span>{displaySafeFilename(file.name)} ({(file.size / 1024).toFixed(1)} KB)</span>
                  <button
                    type="button"
                    onClick={() => {
                      const updatedNewFiles = newFiles.filter((_, i) => i !== index);
                      setUploadedFiles(prev => ({
                        ...prev,
                        [question.id]: updatedNewFiles
                      }));
                    }}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Show existing files if any */}
        {existingFiles.length > 0 && (
          <div className="mt-2">
            <h4 className="text-sm font-medium text-gray-900">{t('lawsuit.existingFiles', 'Existing Files')}:</h4>
            <ul className="mt-1 space-y-1">
              {existingFiles.map((file: any, index: number) => (
                <li key={`existing-${index}`} className="text-sm text-gray-600 flex items-center">
                  {file.isPlaceholder ? (
                    <span className="text-amber-600 flex items-center">
                      ⚠️ {file.originalName || file.filename || file.name || 'File'} 
                      <span className="ml-1 text-amber-500">({t('lawsuit.fileNotFound', 'File not found')})</span>
                    </span>
                  ) : getFileDownloadUrl(file) ? (
                    <a href={getFileDownloadUrl(file)!} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                      {displaySafeFilename(file.originalName || file.filename || file.name || 'File')}
                    </a>
                  ) : (
                    <span>{(file.originalName || file.filename || file.name || 'File').length > 30
                      ? (file.originalName || file.filename || file.name || 'File').slice(0, 27) + '...'
                      : (file.originalName || file.filename || file.name || 'File')}</span>
                  )}
                  {file.size && file.size > 0 && (
                    <span className="ml-2 text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    switch (question.type) {
      case 'text':
        return (
          <div>
            <textarea
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
              rows={3}
              placeholder={t('filing.aiAnalysis.answerPlaceholder', 'Please provide your answer here...')}
            />
            {renderFileUploadSection()}
          </div>
        );
      case 'boolean':
        return (
          <div>
            <div className="mt-1 space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={question.id}
                  value="true"
                  checked={value === true}
                  onChange={() => handleResponseChange(question.id, true)}
                  className="focus:ring-cyan-500 h-4 w-4 text-cyan-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{t('common.yes', 'Yes')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={question.id}
                  value="false"
                  checked={value === false}
                  onChange={() => handleResponseChange(question.id, false)}
                  className="focus:ring-cyan-500 h-4 w-4 text-cyan-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">{t('common.no', 'No')}</span>
              </label>
            </div>
            {renderFileUploadSection()}
          </div>
        );
      case 'multiple_choice':
        return (
          <div>
            <div className="mt-1 space-y-2">
              {question.options?.map((option: string) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={value === option}
                    onChange={() => handleResponseChange(question.id, option)}
                    className="focus:ring-cyan-500 h-4 w-4 text-cyan-600 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
            {renderFileUploadSection()}
          </div>
        );
      case 'date':
        return (
          <div>
            <input
              type="date"
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            />
            {renderFileUploadSection()}
          </div>
        );
      case 'number':
        return (
          <div>
            <input
              type="number"
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            />
            {renderFileUploadSection()}
          </div>
        );
      case 'file_upload':
      case 'document':
        return (
          <div className="space-y-4">
            {/* File upload section */}
            <div className="mt-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('lawsuit.evidence', 'Supporting Evidence')}
              </label>
              <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor={`file-upload-${question.id}`}
                      className="relative cursor-pointer bg-white rounded-md font-medium text-cyan-600 hover:text-cyan-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-cyan-500"
                    >
                      <span>{t('lawsuit.uploadFiles', 'Upload files')}</span>
                      <input
                        id={`file-upload-${question.id}`}
                        name={`file-upload-${question.id}`}
                        type="file"
                        className="sr-only"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                        onChange={(e) => handleFileUpload(question.id, e.target.files)}
                      />
                    </label>
                    <p className="pl-1">{t('lawsuit.orDragDrop', 'or drag and drop')}</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('lawsuit.fileTypes', 'PDF, DOC, DOCX, TXT, JPG, PNG, GIF up to 10MB each')}
                  </p>
                </div>
              </div>
              {/* Show new uploaded files */}
              {newFiles.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-gray-900">{t('lawsuit.uploadedFiles', 'Uploaded Files')}:</h4>
                  <ul className="mt-1 space-y-1">
                    {newFiles.map((file: File, index: number) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center justify-between">
                        <span>{displaySafeFilename(file.name)} ({(file.size / 1024).toFixed(1)} KB)</span>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedNewFiles = newFiles.filter((_, i) => i !== index);
                            setUploadedFiles(prev => ({
                              ...prev,
                              [question.id]: updatedNewFiles
                            }));
                          }}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Show existing files if any */}
              {existingFiles.length > 0 && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-gray-900">{t('lawsuit.existingFiles', 'Existing Files')}:</h4>
                  <ul className="mt-1 space-y-1">
                    {existingFiles.map((file: any, index: number) => (
                      <li key={`existing-${index}`} className="text-sm text-gray-600 flex items-center">
                        {file.isPlaceholder ? (
                          <span className="text-amber-600 flex items-center">
                            ⚠️ {file.originalName || file.filename || file.name || 'File'} 
                            <span className="ml-1 text-amber-500">({t('lawsuit.fileNotFound', 'File not found')})</span>
                          </span>
                        ) : getFileDownloadUrl(file) ? (
                          <a href={getFileDownloadUrl(file)!} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                            {file.originalName || file.filename || file.name || 'File'}
                          </a>
                        ) : (
                          <span className="text-gray-600">
                            {file.originalName || file.filename || file.name || 'File'}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Text input section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('filing.aiAnalysis.textResponse', 'Text Response (Optional)')}
              </label>
              <textarea
                value={value}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                rows={3}
                placeholder={t('filing.aiAnalysis.textPlaceholder', 'Provide any additional information or context...')}
              />
            </div>
          </div>
        );
      default:
        return (
          <div>
            <input
              type="text"
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
            />
            {renderFileUploadSection()}
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('errors.lawsuitNotFound', 'Lawsuit not found')}
          </h3>
        </div>
      </div>
    );
  }

  const followUpQuestions = lawsuit.followUpQuestions || [];
  
  // Get the latest version of each question (either from questionsWithUpdates or original)
  const getQuestionWithUpdates = (question: AIQuestion): AIQuestion => {
    return questionsWithUpdates[question.id] || question;
  };
  
  // Helper function to get actual file objects from question.files string array
  const getQuestionFiles = (question: AIQuestion) => {
    if (!question.files || !lawsuit?.files) return [];
    
    const matchedFiles = question.files
      .map(fileName => {
        const foundFile = lawsuit.files?.find(file => 
          file.id === fileName || 
          file.filename === fileName || 
          file.originalName === fileName
        );
        
        if (!foundFile) {
          // Return a placeholder file object instead of undefined to preserve the count
          return {
            id: fileName,
            filename: fileName,
            originalName: fileName,
            mimeType: 'unknown',
            size: 0,
            url: null,
            uri: null,
            uploadedBy: '',
            uploadedAt: new Date(),
            isPlaceholder: true
          };
        }
        
        return foundFile;
      });
    
    return matchedFiles;
  };
  
  // Helper function to get the proper download URL for a file
  const getFileDownloadUrl = (file: any): string | null => {
    // Priority: downloadUrl > url > constructed gcsPath URL > null
    if (file.downloadUrl) return file.downloadUrl;
    if (file.url) return file.url;
    if (file.gcsPath) return `/api/files/download/${encodeURIComponent(file.gcsPath)}`;
    return null;
  };
  
  const unansweredQuestions = followUpQuestions.filter((q: AIQuestion) => {
    // Question is unanswered if it hasn't been submitted yet
    return !submittedQuestions.has(q.id);
  });
  
  const answeredQuestions = followUpQuestions.filter((q: AIQuestion) => {
    // Question is answered if it has been submitted
    return submittedQuestions.has(q.id);
  });
  


  return (
    <>
      <Helmet>
        <title>{`${t('lawsuit.strengthenCase', 'Strengthen Case')} - ${lawsuit.title} - tvia.ai`}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center mb-4">
                <CogIcon className="h-6 w-6 text-cyan-500 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('lawsuit.strengthenCase', 'Strengthen Case')}
                </h1>
              </div>
              <p className="text-gray-600 mb-4">
                {t('lawsuit.strengthenCaseDescription', 'Answer additional questions to strengthen your case and improve your chances of success.')}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  {lawsuit.title}
                </h3>
                <p className="text-sm text-blue-800">
                  {t('lawsuit.currentStrength', 'Current case strength')}: {lawsuit.aiAnalysis?.strengthScore || 0}/100
                </p>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('lawsuit.questionProgress', 'Question Progress')}
                </h2>
                <span className="text-sm text-gray-500">
                  {answeredQuestions.length}/{followUpQuestions.length} {t('common.completed', 'completed')}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: followUpQuestions.length > 0 
                      ? `${(answeredQuestions.length / followUpQuestions.length) * 100}%` 
                      : '0%'
                  }}
                ></div>
              </div>
              {followUpQuestions.length > 0 && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center">
                    <QuestionMarkCircleIcon className="h-4 w-4 text-orange-500 mr-2" />
                    <span className="text-gray-600">
                      {unansweredQuestions.length} {t('lawsuit.unanswered', 'unanswered')}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <CheckIcon className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-gray-600">
                      {answeredQuestions.length} {t('lawsuit.completed', 'completed')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Unanswered Questions */}
          {unansweredQuestions.length > 0 && (
            <div className="bg-white shadow rounded-lg mb-8">
              <div className="px-6 py-8">
                <div className="flex items-center mb-6">
                  <QuestionMarkCircleIcon className="h-6 w-6 text-orange-500 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('lawsuit.unansweredQuestions', 'Unanswered Questions')}
                  </h2>
                  <span className="ml-auto text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                    {unansweredQuestions.length} {t('lawsuit.remaining', 'remaining')}
                  </span>
                </div>
                <div className="space-y-6">
                  {unansweredQuestions.map((question: AIQuestion) => (
                    <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {question.question}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {question.legalPurpose && (
                        <p className="text-xs text-gray-500 mb-3">
                          <strong>{t('lawsuit.legalPurpose', 'Legal Purpose')}:</strong> {question.legalPurpose}
                        </p>
                      )}
                      {renderQuestionInput(question)}
                    </div>
                  ))}
                </div>
                
                {(Object.keys(questionsWithUpdates).some(questionId => {
                  const question = questionsWithUpdates[questionId];
                  const hasTextAnswer = question.answer !== undefined && question.answer !== null && question.answer !== '';
                  const hasNewFiles = uploadedFiles[questionId]?.length > 0;
                  return hasTextAnswer || hasNewFiles;
                }) || Object.keys(uploadedFiles).some(questionId => uploadedFiles[questionId]?.length > 0)) && (
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => navigate(`/lawsuit/${id}`)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                      onClick={handleSubmitResponses}
                      disabled={submitResponsesMutation.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
                    >
                      {submitResponsesMutation.isPending ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          {t('common.submitting', 'Submitting...')}
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2" />
                          {t('lawsuit.submitAndUpdate', 'Submit & Update Case')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Completed Questions - Collapsible and Editable Section */}
          {answeredQuestions.length > 0 && (
            <div className="bg-white shadow rounded-lg mb-8">
              <div className="px-6 py-6">
                <button
                  onClick={() => setAnsweredQuestionsCollapsed(!answeredQuestionsCollapsed)}
                  className="flex items-center justify-between w-full text-left hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                >
                  <div className="flex items-center">
                    <CheckIcon className="h-6 w-6 text-green-500 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('lawsuit.completedQuestions', 'Completed Questions')}
                    </h2>
                    <span className="ml-3 text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      {answeredQuestions.length} {t('lawsuit.completed', 'completed')}
                    </span>
                  </div>
                  {answeredQuestionsCollapsed ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {!answeredQuestionsCollapsed && (
                  <div className="mt-6 space-y-6">
                    {answeredQuestions.map((question: AIQuestion) => {
                      const updatedQuestion = getQuestionWithUpdates(question);
                      // Get actual file objects by matching string array with lawsuit files
                      const existingFiles = getQuestionFiles(updatedQuestion);
                      const newFiles = uploadedFiles[question.id] || [];
                      const allFiles = [...existingFiles, ...newFiles];
                      
                      return (
                        <div key={question.id} className="border rounded-lg p-4 border-green-200 bg-green-50">
                          <label className="block text-sm font-medium text-gray-900 mb-2">
                            {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {question.legalPurpose && (
                            <p className="text-xs text-gray-500 mb-3">
                              <strong>{t('lawsuit.legalPurpose', 'Legal Purpose')}:</strong> {question.legalPurpose}
                            </p>
                          )}
                          <div className="space-y-2">
                            {editingQuestionId === question.id ? (
                              <>
                                {/* Editable input for the answer and file upload */}
                                {renderQuestionInput(question)}
                                <div className="flex gap-2 mt-2">
                                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] flex items-center justify-center" onClick={() => {
                                    setEditingQuestionId(null);
                                  }}>{t('common.save', 'Save')}</button>
                                  <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px] flex items-center justify-center" onClick={() => {
                                    // Reset any unsaved changes for this question
                                    setEditingQuestionId(null);
                                    setUploadedFiles(prev => ({ ...prev, [question.id]: [] }));
                                  }}>{t('common.cancel', 'Cancel')}</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-sm text-gray-700">
                                  <strong>{t('lawsuit.answer', 'Answer')}:</strong> {
                                    (question.type === 'file_upload' || question.type === 'document')
                                      ? allFiles.length === 0
                                        ? t('lawsuit.noFilesUploaded', 'No files uploaded')
                                        : allFiles.length > 0 && (!updatedQuestion.answer || updatedQuestion.answer === '')
                                          ? t('lawsuit.answeredWithFiles', 'Answered with uploaded files')
                                          : updatedQuestion.answer || ''
                                      : typeof updatedQuestion.answer === 'boolean'
                                        ? (updatedQuestion.answer ? t('common.yes', 'Yes') : t('common.no', 'No'))
                                        : typeof updatedQuestion.answer === 'object' && updatedQuestion.answer !== null
                                          ? updatedQuestion.answer.description || ''
                                          : updatedQuestion.answer || ''
                                }
                                </p>
                                {(question.type === 'file_upload' || question.type === 'document') && allFiles.length > 0 && (
                                  <div className="mt-2">
                                    <h4 className="text-xs font-medium text-gray-900 mb-1">{t('lawsuit.uploadedFiles', 'Uploaded Files')}:</h4>
                                    <ul className="mt-1 space-y-1">
                                      {allFiles.map((file: any, idx: number) => (
                                        <li key={idx} className="text-xs text-gray-600 flex items-center">
                                          {file.isPlaceholder ? (
                                            <span className="text-amber-600 flex items-center">
                                              ⚠️ {file.originalName || file.filename || file.name || t('lawsuit.file', 'File')} 
                                              <span className="ml-1 text-amber-500">({t('lawsuit.fileNotFound', 'File not found')})</span>
                                            </span>
                                          ) : getFileDownloadUrl(file) ? (
                                            <a href={getFileDownloadUrl(file)!} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                                              {(file.originalName || file.filename || file.name || t('lawsuit.file', 'File')).length > 30
                                                ? (file.originalName || file.filename || file.name || t('lawsuit.file', 'File')).slice(0, 27) + '...'
                                                : (file.originalName || file.filename || file.name || t('lawsuit.file', 'File'))}
                                            </a>
                                          ) : (
                                            <span className="text-gray-600">
                                              {(file.originalName || file.filename || file.name || t('lawsuit.file', 'File')).length > 30
                                                ? (file.originalName || file.filename || file.name || t('lawsuit.file', 'File')).slice(0, 27) + '...'
                                                : (file.originalName || file.filename || file.name || t('lawsuit.file', 'File'))}
                                            </span>
                                          )}
                                          {file.size && file.size > 0 && (
                                            <span className="ml-2 text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] flex items-center justify-center" onClick={() => {
                                  setEditingQuestionId(question.id);
                                }}>{t('common.edit', 'Edit')}</button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generate More Questions */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center mb-4">
                <SparklesIcon className="h-6 w-6 text-purple-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('lawsuit.generateMoreQuestions', 'Generate Additional Questions')}
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                {t('lawsuit.generateQuestionsDescription', 'Let our AI generate more specific questions based on your case details to further strengthen your position.')}
              </p>
              
              {hasGeneratedQuestions && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                    <p className="text-sm text-green-800">
                      {t('lawsuit.questionsGenerated', 'New questions have been generated! Check the questions above.')}
                    </p>
                  </div>
                </div>
              )}
              
              <button
                onClick={handleGenerateQuestions}
                disabled={generateQuestionsMutation.isPending || isGeneratingQuestions || hasGeneratedQuestions}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                {(generateQuestionsMutation.isPending || isGeneratingQuestions) ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    {t('lawsuit.generatingQuestions', 'Generating Questions...')}
                  </>
                ) : hasGeneratedQuestions ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    {t('lawsuit.questionsGenerated', 'Questions Generated')}
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    {t('lawsuit.generateQuestions', 'Generate 10 Questions')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Premium AI Analysis */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center mb-4">
                <SparklesIcon className="h-6 w-6 text-yellow-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('lawsuit.premiumAnalysis', 'Premium AI Analysis')}
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                {t('lawsuit.premiumAnalysisDescription', 'Upgrade your case analysis with our premium AI models for enhanced legal insights, deeper precedent analysis, and more comprehensive documentation.')}
              </p>
              
              {lawsuit.isPremiumAnalysis ? (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    ✨ {t('lawsuit.premiumAnalysisEnabled', 'Premium AI Analysis Enabled')}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {t('lawsuit.premiumAnalysisDate', 'Your case was upgraded with premium AI analysis.')}
                  </p>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex">
                    <SparklesIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-yellow-800 font-medium">
                        {t('lawsuit.premiumFeatures', 'Premium Features Include:')}
                      </p>
                      <ul className="text-xs text-yellow-700 mt-2 space-y-1">
                        <li>• Gemini Pro AI model for superior analysis</li>
                        <li>• Deeper legal precedent research</li>
                        <li>• Advanced risk assessment</li>
                        <li>• Enhanced document generation</li>
                        <li>• Strategic litigation advice</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={lawsuit.isPremiumAnalysis ? undefined : handleOpenPremiumModal}
                disabled={lawsuit.isPremiumAnalysis}
                className="flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {lawsuit.isPremiumAnalysis ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    {t('lawsuit.premiumEnabled', 'Premium Analysis Enabled')}
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    {t('lawsuit.usePremiumAI', 'Use Premium AI Models')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Update Case Section */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center mb-4">
                <ArrowPathIcon className="h-6 w-6 text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('lawsuit.updateCase', 'Update Case')}
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                {t('lawsuit.updateCaseDescription', 'Add additional evidence, responses from the accused party, or any other information to strengthen your case.')}
              </p>
              
              <button
                onClick={() => setShowUpdateCaseModal(true)}
                className="flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                {t('lawsuit.addCaseUpdate', 'Add Case Update')}
              </button>
            </div>
          </div>

        </div>
      </div>


      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        onUpgrade={handleUpgradeToPremium}
        isUpgrading={premiumAnalysisMutation.isPending}
      />

      {/* Premium Upgrade Success Modal */}
      <PremiumUpgradeSuccessModal
        isOpen={showPremiumSuccessModal}
        onClose={() => {
          setShowPremiumSuccessModal(false);
          navigate(`/lawsuit/${id}`);
        }}
        data={premiumUpgradeData || {}}
      />

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('lawsuit.caseUpdateProgress', 'Case Update Progress')}
                </h3>
                <button
                  onClick={() => setShowProgressModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <CaseUpdateProgressBar 
                steps={progressSteps}
                currentStep={currentStepId || undefined}
                error={processError || undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Update Case Modal */}
      {showUpdateCaseModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('lawsuit.updateCase', 'Update Case')}
                </h3>
                <button
                  onClick={() => setShowUpdateCaseModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Update Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('lawsuit.updateType', 'Update Type')} *
                  </label>
                  <select
                    value={updateCaseData.updateType}
                    onChange={(e) => setUpdateCaseData(prev => ({ ...prev, updateType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="evidence">{t('lawsuit.evidence', 'Evidence')}</option>
                    <option value="accused_party_response">{t('lawsuit.accusedPartyResponse', 'Accused Party Response')}</option>
                    <option value="additional_information">{t('lawsuit.additionalInformation', 'Additional Information')}</option>
                    <option value="witness_statement">{t('lawsuit.witnessStatement', 'Witness Statement')}</option>
                    <option value="expert_opinion">{t('lawsuit.expertOpinion', 'Expert Opinion')}</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('lawsuit.description', 'Description')} *
                  </label>
                  <textarea
                    value={updateCaseData.description}
                    onChange={(e) => setUpdateCaseData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('lawsuit.descriptionPlaceholder', 'Describe the additional information, evidence, or response...')}
                  />
                </div>

                {/* Accused Party Response (only show if update type is accused_party_response) */}
                {updateCaseData.updateType === 'accused_party_response' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('lawsuit.accusedPartyResponse', 'Accused Party Response')}
                    </label>
                    <textarea
                      value={updateCaseData.accusedPartyResponse}
                      onChange={(e) => setUpdateCaseData(prev => ({ ...prev, accusedPartyResponse: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('lawsuit.accusedPartyResponsePlaceholder', 'Enter the response from the accused party...')}
                    />
                  </div>
                )}

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('lawsuit.supportingFiles', 'Supporting Files')}
                  </label>
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="update-case-file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                        >
                          <span>{t('lawsuit.uploadFiles', 'Upload files')}</span>
                          <input
                            id="update-case-file-upload"
                            name="update-case-file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                            onChange={(e) => handleUpdateCaseFileUpload(e.target.files)}
                          />
                        </label>
                        <p className="pl-1">{t('lawsuit.orDragDrop', 'or drag and drop')}</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('lawsuit.fileTypes', 'PDF, DOC, DOCX, TXT, JPG, PNG, GIF up to 10MB each')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Show uploaded files */}
                  {updateCaseData.files.length > 0 && (
                    <div className="mt-2">
                      <h4 className="text-sm font-medium text-gray-900">{t('lawsuit.uploadedFiles', 'Uploaded Files')}:</h4>
                      <ul className="mt-1 space-y-1">
                        {updateCaseData.files.map((file: File, index: number) => (
                          <li key={index} className="text-sm text-gray-600 flex items-center justify-between">
                            <span>{displaySafeFilename(file.name)} ({(file.size / 1024).toFixed(1)} KB)</span>
                            <button
                              type="button"
                              onClick={() => removeUpdateCaseFile(index)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowUpdateCaseModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleUpdateCaseSubmit}
                    disabled={updateCaseMutation.isPending || analyzeCaseMutation.isPending || generateDocumentsMutation.isPending || !updateCaseData.description.trim()}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {(updateCaseMutation.isPending || analyzeCaseMutation.isPending || generateDocumentsMutation.isPending) ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        {t('lawsuit.submitUpdate', 'Submit Update')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StrengthenCasePage;